import React, { useEffect, useState, useRef, useMemo } from 'react';
import '../../style.css';
import Avatar from '@mui/material/Avatar';
import { useSelector, useDispatch } from 'react-redux';
import { toast, ToastContainer } from 'react-toastify';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Menu,
  MenuItem,
  IconButton,
  Box,
  Typography,
  Button
} from '@mui/material';
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { suspendedListApi } from '../../../../store/features/suspendedAgentList';
import { unSupendedApi } from '../../../../store/features/unSuspendedUser';
import { ReactComponent as EmptyGroupIcon } from '../../../../assets/images/emptyGroupIcon.svg';
import SearchInput from '../../../common/SearchInputField';
import { useNavigate } from 'react-router-dom';
import DeletePopUp from '../../DeletePopUp';
import { deleteUserApi } from '../../../../store/features/deleteCoUser';
import TeamManagementSkeleton from '../TeamManagementSkeleton';
import {
  getTeamMgmtPeek,
  setTeamMgmtPeek,
  TEAM_MGMT_CACHE_TABS,
} from '../../../../utils/teamManagementViewCache';

const chracterPosition = (alphabet) => {
  const alphabets = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const positionOfAlphabet = alphabets.indexOf(alphabet) + 1;
  return positionOfAlphabet;
};

const colorCodesForAvatar = [
  '#405261', '#FF4D4F', '#FA541C', '#FA8C16', '#FAAD14', '#EB2F96',
  '#FADB14', '#A0D911', '#52C41A', '#5CDBD3', '#9254DE', '#F759AB',
  '#2F54EB', '#722ED1', '#1890FF', '#13C2C2', '#2DB7F5', '#19BE6B',
  '#30A745', '#FA8C16', '#FAAD14', '#9254DE', '#F759AB', '#13C2C2',
  '#722ED1', '#2DB7F5'
];

function buildSuspendedAgentRows(rawList) {
  if (!Array.isArray(rawList) || !rawList.length) return [];
  return rawList.map((item) => ({
    id: item?.user.id,
    img: item?.user.agent_image,
    name: item.user.full_name,
    email: item.user.email,
    role_name: item.user.role_name,
    selected: false,
  }));
}

function SuspendedMain(props) {
  const userData = useSelector((state) => state.loginToken);
  const businessId = userData?.response?.user?.business_id;
  const suspendedPeek = getTeamMgmtPeek(businessId, TEAM_MGMT_CACHE_TABS.SUSPENDED_USERS);
  const initialSuspendedRows =
    suspendedPeek?.rawData !== undefined
      ? buildSuspendedAgentRows(suspendedPeek.rawData)
      : [];

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [data, setData] = useState(initialSuspendedRows);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredData, setFilteredData] = useState(initialSuspendedRows);
  const [activeRowId, setActiveRowId] = useState(null);
  const [isLoading, setIsLoading] = useState(suspendedPeek?.rawData === undefined);
  const [deleteUser, setDeleteUser] = useState(false);
  const [teamUserId, setTeamUserId] = useState('');
  const [toolbarHeight, setToolbarHeight] = useState(0);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const toolbarRef = useRef(null);

  const uID = userData.response?.user?._id;
  const uRole = userData.response?.user?.role_name;

  const currentLanguage = useSelector(
    (state) => state.language.currentLanguage,
  );

  const t = useSelector(
    (state) => state.language.translations[currentLanguage],
  );

  // Calculate pagination - moved before conditional return
  const paginatedData = filteredData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Calculate dynamic table height - moved before conditional return
  const tableContainerHeight = useMemo(() => {
    return `calc(100vh - ${349 + toolbarHeight}px)`;
  }, [toolbarHeight]);

  useEffect(() => {
    if (toolbarRef.current) {
      setToolbarHeight(toolbarRef.current.offsetHeight);
    }
  }, [filteredData?.length]);

  const getSuspendedList = async () => {
    const listsuspendedUser = dispatch(suspendedListApi());

    listsuspendedUser
      .then(async (data) => {
        if (typeof data.payload === 'undefined') {
          toast.error('Some Error Occured. Please try again later.', {
            position: 'top-right',
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: 'light',
          });
          setIsLoading(false);
        } else if (data?.payload?.status === 200) {
          const list = Array.isArray(data.payload.data) ? data.payload.data : [];
          if (businessId) {
            setTeamMgmtPeek(businessId, TEAM_MGMT_CACHE_TABS.SUSPENDED_USERS, list);
          }
          const outputarray = buildSuspendedAgentRows(list);
          setFilteredData(outputarray);
          setData(outputarray);
          setIsLoading(false);
        } else if (data?.payload?.status === 500) {
          setIsLoading(false);
          toast.error('Some Error Occured. Please try again later.', {
            position: 'top-right',
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: 'light',
          });
        } else if (data?.payload?.status === 401) {
          setIsLoading(false);
          toast.error('Login Expired. Please login and try again.', {
            position: 'top-right',
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: 'light',
          });
          navigate('/login');
        }
      })
      .catch(async (error) => {
        setIsLoading(false);
        toast.error(error, {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: 'light',
        });
      });
  };

  const unSuspendedHandler = async (rowId) => {
    const unSuspendedList = dispatch(
      unSupendedApi({
        requestHeaders: { id: rowId },
      }),
    );
    unSuspendedList
      .then(async (data) => {
        if (typeof data.payload === 'undefined') {
          toast.error('Some Error Occured. Please try again later.', {
            position: 'top-right',
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: 'light',
          });
        } else if (data.payload.status === 200) {
          toast.success(data?.payload?.message);
          getSuspendedList();
          props.onSuspenseChange();
          handleCloseMenu();
        } else if (data?.payload?.status === 500) {
          toast.error('Some Error Occured. Please try again later.', {
            position: 'top-right',
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: 'light',
          });
        }
      })
      .catch(async (error) => {
        toast.error(error, {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: 'light',
        });
      });
  };

  useEffect(() => {
    getSuspendedList();
  }, []);

  const handleSearchInputChange = (e) => {
    const query = e.target.value.toLowerCase();
    setSearchQuery(query);
    const filteredResults = data.filter((row) =>
      row.name.toLowerCase().includes(query) || row.email.toLowerCase().includes(query)
    );
    setFilteredData(filteredResults);
    setPage(0); // Reset to first page on search
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenMenu = (event, rowId) => {
    setAnchorEl(event.currentTarget);
    setSelectedRowId(rowId);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSelectedRowId(null);
  };

  const handleEditProfile = (userId) => {
    const url = `/userupdate/${userId}`;
    navigate(url);
    handleCloseMenu();
  };

  const deleteUserHandler = async () => {
    const deleteuserList = dispatch(
      deleteUserApi({
        requestHeaders: { id: teamUserId },
      }),
    );
    deleteuserList
      .then(async (data) => {
        if (typeof data.payload === 'undefined') {
          toast.error('Some Error Occured. Please try again later.', {
            position: 'top-right',
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: 'light',
          });
        } else if (data.payload.status === 200) {
          toast.success(data?.payload?.message);
          getSuspendedList();
          handleCloseMenu();
        } else if (data?.payload?.status === 500) {
          toast.error('Some Error Occured. Please try again later.', {
            position: 'top-right',
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: 'light',
          });
        }
      })
      .catch(async (error) => {
        toast.error(error, {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: 'light',
        });
      });
  };

  if (isLoading) {
    return <TeamManagementSkeleton />;
  }

  return (
    <>
      <div className="team-container" ref={toolbarRef}>
        <SearchInput
          title=""
          placeholder="Search"
          className="SearchInputField-teamsection"
          value={searchQuery}
          onChange={handleSearchInputChange}
        />
      </div>

      {parseInt(filteredData?.length, 10) < 1 && (
        <div className="NoSuspendedAgent-main-Conatiner">
          <div className="TeamChatBot-Main-Container">
            <EmptyGroupIcon />
            <p className="Chatbot-devploment">{t.NOAVAILABLEAGENTSSUSPENDED}</p>
          </div>
        </div>
      )}

      {parseInt(filteredData?.length, 10) > 0 && (
        <Box sx={{ width: '100%' }}>
          <p className="Team-total-active-user">
            Suspended ({filteredData.length})
          </p>

          <TableContainer
            sx={{
              boxShadow: "none",
              flex: 1,
              marginTop: "2px",
              marginBottom: "40px",
              height: tableContainerHeight,
              overflowY: "auto",
            }}
          >
            <Table stickyHeader sx={{ minWidth: 750 }} aria-labelledby="tableTitle">
              <TableHead>
                <TableRow sx={{

                  bottom: 0,
                  '& th': {

                    color: '#405261',
                    fontFamily: 'Inter',
                    fontSize: '12px',
                    fontWeight: 400,


                  },
                }}>
                  <TableCell sx={{

                    paddingTop: '0px',
                    paddingBottom: '9px !important',
                    zIndex: deleteUser
                      ? 'unset'
                      : 7,
                    lineHeight: '0',
                  }} >{t.NAMETB}</TableCell>
                  <TableCell sx={{

                    paddingTop: '0px',
                    paddingBottom: '9px !important',
                    zIndex: deleteUser
                      ? 'unset'
                      : 7,
                    lineHeight: '0',
                  }} >{t.EMAILTB}</TableCell>
                  <TableCell sx={{

                    paddingTop: '0px',
                    paddingBottom: '9px !important',
                    zIndex: deleteUser
                      ? 'unset'
                      : 7,
                    lineHeight: '0',
                  }}>{t.ROLETB}</TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedData.map((row) => (
                  <TableRow
                    key={row.id}
                    sx={{
                      "&:hover": {
                        backgroundColor: "#F9FAFA",
                      },
                      "&:hover td": {
                        backgroundColor: "#F9FAFA",
                      },
                      "& td": {
                        height: "4rem", py: 0, verticalAlign: "middle",
                        color: "#405261",
                        fontFamily: "Inter",
                        fontSize: "14px",
                        fontWeight: 400,


                      },
                    }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        {row.img ? (
                          <Avatar

                            src={row.img}
                            alt="users logo"
                            sx={{
                              width: 32,
                              height: 32,
                            }}
                          />
                        ) : (
                          <Avatar
                            sx={{
                              bgcolor:
                                colorCodesForAvatar[
                                chracterPosition(
                                  row.name?.substring(0, 1).toUpperCase(),
                                )
                                ],
                              width: 32,
                              height: 32,
                              fontSize: '16px',
                            }}
                          >
                            {row.name?.substring(0, 1).toUpperCase()}
                          </Avatar>
                        )}
                        <Box>
                          <div className="team-table-rowtext">{row.name}</div>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>{row.email}</TableCell>
                    <TableCell>
                      {row.role_name.charAt(0).toUpperCase() + row.role_name.slice(1)}
                    </TableCell>
                    <TableCell align="right">
                      {(uRole === 'admin' || uRole === 'admin_with_billing' || row.id === uID) && (
                        <IconButton
                          onClick={(e) => handleOpenMenu(e, row.id)}
                          size="small"
                        >
                          <MoreHorizIcon />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>


          {!deleteUser && <Box
            sx={{
              position: "sticky",
              bottom: 0,
              left: 0,
              width: "100%",
              background: "#fff",
              zIndex: 10,
              padding: "16px 24px",
              borderTop: "1px solid #E5E9EC",
            }}
          >
            <TablePagination
              component="div"
              count={filteredData.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={[]}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelDisplayedRows={({ from, to, count }) => (
                <Typography
                  sx={{
                    color: "#405261",
                    fontFamily: "Inter",
                    fontSize: "14px",
                    fontStyle: "normal",
                    fontWeight: 400,
                    lineHeight: "18px",
                  }}
                >
                  Suspended Agents  {from}-{to}
                </Typography>
              )}
              ActionsComponent={(props) => (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    marginLeft: "auto",
                  }}
                >
                  <Button
                    onClick={(e) => props.onPageChange(e, props.page - 1)}
                    disabled={props.page === 0}
                    sx={{
                      width: "128px",
                      height: "40px",
                      flexShrink: 0,
                      borderRadius: "8px",
                      border: "1px solid #D5DCE1",
                      background: "#FFF",
                      boxShadow: "0px 1px 2px 0px rgba(6, 21, 34, 0.05)",
                      color: "#405261",
                      fontSize: "14px",
                      fontWeight: 500,
                      lineHeight: "18px",
                      textTransform: "none",
                      "&:not(.Mui-disabled):hover": {
                        backgroundColor: "#FFFFFF",
                        borderColor: "#C5CED6",
                      },
                      "&.Mui-disabled": {
                        color: "#93A1B0",
                        border: "1px solid #C5CED6",
                        bgcolor: "#F9FAFA",
                      },
                    }}
                  >
                    Previous
                  </Button>
                  <Button
                    onClick={(e) => props.onPageChange(e, props.page + 1)}
                    disabled={
                      props.page >= Math.ceil(props.count / rowsPerPage) - 1
                    }
                    sx={{
                      width: "88px",
                      height: "40px",
                      flexShrink: 0,
                      borderRadius: "8px",
                      border: "1px solid #D5DCE1",
                      boxShadow: "0px 1px 2px 0px rgba(6, 21, 34, 0.05)",
                      color: "#405261",
                      backgroundColor: "#FFFFFF",
                      fontSize: "14px",
                      fontWeight: 500,
                      lineHeight: "18px",
                      textTransform: "none",
                      "&:not(.Mui-disabled):hover": {
                        backgroundColor: "#FFFFFF",
                        borderColor: "#C5CED6",
                      },
                      "&.Mui-disabled": {
                        color: "#93A1B0",
                        border: "1px solid #C5CED6",
                        bgcolor: "#F9FAFA",
                      },
                    }}
                  >
                    Next
                  </Button>
                </Box>
              )}
              sx={{
                ".MuiTablePagination-toolbar": {
                  padding: 0,
                  minHeight: "unset",
                  display: "flex",
                  justifyContent: "space-between",
                },
                ".MuiTablePagination-spacer": {
                  display: "none",
                },
                ".MuiTablePagination-selectLabel": {
                  display: "none",
                },
                ".MuiTablePagination-select": {
                  display: "none",
                },
                ".MuiTablePagination-selectIcon": {
                  display: "none",
                },
              }}
            />
          </Box>}

          {/* Menu for actions */}
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleCloseMenu}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            PaperProps={{
              elevation: 0,
              sx: {
                filter: "none",
                color: '#405261',
                mt: 1.5,
                width: 200,
                paddingX: "8px",
                borderRadius: "8px", // ✅ your custom border radius
                border: "1px solid #D5DCE1", // ✅ your custom border

                boxShadow: "0 15px 20px 0 rgba(6, 21, 34, 0.05)", // ✅ soft shadow
                "& .MuiMenuItem-root": {
                  px: "8px",
                  py: 1,
                  fontSize: '14px',
                  fontFamily: 'Inter',
                  color: '#405261',

                  "&:hover": { color: "#061522", borderRadius: "8px", background: "#F9FAFA !important" },
                },
              },
            }}
          >
            <MenuItem onClick={() => handleEditProfile(selectedRowId)}>
              {t.EDITPROFILE}
            </MenuItem>
            <MenuItem onClick={() => unSuspendedHandler(selectedRowId)}>
              {t.UNSUBAGENT}
            </MenuItem>
            <MenuItem
              onClick={() => {
                setTeamUserId(selectedRowId);
                setDeleteUser(true);
                handleCloseMenu();
              }}
              sx={{
                color: '#FF4D4F !important',
                "&:hover": {
                  color: '#FF4D4F !important', // ✅ stays red no matter what
                  background: 'rgba(255, 77, 79, 0.08)',
                  borderRadius: '8px',
                },
              }}

            >
              {t.DELETEALL}
            </MenuItem>
          </Menu>
        </Box>
      )}

      {deleteUser && (
        <DeletePopUp
          onCancel={() => setDeleteUser(false)}
          onDelete={() => {
            deleteUserHandler();
            setDeleteUser(false);
          }}
        />
      )}
    </>
  );
}

export default SuspendedMain;